import React, { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import { useAuth } from '../context/AuthContext';
import axios from 'axios';
import { FiAward, FiChevronDown, FiChevronUp, FiCheckCircle, FiClock, FiUsers, FiBook, FiSearch } from 'react-icons/fi';
import CertificateModal from './CertificateModal';

import { API_URL, getAssetUrl } from '../config';

function TrainingCompletions() {
  const { user } = useAuth();
  const navigate = useNavigate();
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(true);
  const [expandedUsers, setExpandedUsers] = useState(new Set());
  const [searchTerm, setSearchTerm] = useState('');
  const [filterStatus, setFilterStatus] = useState('all'); // all, completed, inProgress, notStarted
  const [showCertificate, setShowCertificate] = useState(false);
  const [selectedCompletion, setSelectedCompletion] = useState(null);

  useEffect(() => {
    loadCompletions();
  }, []);

  const loadCompletions = async () => {
    try {
      const response = await axios.get(`${API_URL}/trainings/completions/all`);
      setData(response.data);
    } catch (error) {
      console.error('Error loading completions:', error);
    } finally {
      setLoading(false);
    }
  };

  const toggleUser = (userId) => {
    setExpandedUsers(prev => {
      const newSet = new Set(prev);
      if (newSet.has(userId)) {
        newSet.delete(userId);
      } else {
        newSet.add(userId);
      }
      return newSet;
    });
  };

  const getImageUrl = (imagePath) => {
    if (!imagePath) return null;
    if (imagePath.startsWith('http')) return imagePath;
    return getAssetUrl(imagePath);
  };

  const getUserDisplayName = (u) => {
    if (u.firstName && u.lastName) return `${u.firstName} ${u.lastName}`;
    if (u.firstName) return u.firstName;
    return u.username;
  };

  if (loading) {
    return (
      <div className="min-h-screen bg-gray-50 flex items-center justify-center">
        <div className="text-center">
          <div className="inline-block animate-spin rounded-full h-12 w-12 border-b-2 border-[#3c79bb] mb-4"></div>
          <div className="text-gray-900 font-semibold">Loading completion data...</div>
        </div>
      </div>
    );
  }

  if (!data) {
    return (
      <div className="min-h-screen bg-gray-50 flex items-center justify-center">
        <div className="text-center">
          <h2 className="text-xl font-semibold text-gray-700 mb-2">Error loading data</h2>
          <button onClick={loadCompletions} className="text-[#3c79bb] hover:underline font-medium">
            Try Again
          </button>
        </div>
      </div>
    );
  }

  // Filter users
  const filteredUsers = data.users.filter(u => {
    // Search filter
    const name = getUserDisplayName(u.user).toLowerCase();
    const email = (u.user.email || '').toLowerCase();
    const matchesSearch = name.includes(searchTerm.toLowerCase()) || email.includes(searchTerm.toLowerCase());

    // Status filter
    let matchesStatus = true;
    if (filterStatus === 'completed') {
      matchesStatus = u.totalCompleted > 0;
    } else if (filterStatus === 'inProgress') {
      matchesStatus = u.totalInProgress > 0;
    } else if (filterStatus === 'notStarted') {
      matchesStatus = u.totalCompleted === 0 && u.totalInProgress === 0;
    }

    return matchesSearch && matchesStatus;
  });

  // Summary stats
  const totalUsers = data.users.length;
  const usersWithCompletions = data.users.filter(u => u.totalCompleted > 0).length;
  const usersInProgress = data.users.filter(u => u.totalInProgress > 0 && u.totalCompleted < data.totalTrainings).length;
  const totalCertificates = data.users.reduce((sum, u) => sum + u.totalCompleted, 0);

  return (
    <div className="pb-8">
      <header className="glass-nav border-b border-white/20">
        <div className="max-w-7xl mx-auto px-3 sm:px-4 lg:px-6 xl:px-8 py-4 sm:py-5">
          <h1 className="text-xl sm:text-2xl lg:text-3xl font-bold text-gray-900">Training Completions</h1>
          <p className="text-sm sm:text-base text-gray-700 mt-1 font-medium">View all users' training progress and certificates</p>
        </div>
      </header>

      <main className="max-w-7xl mx-auto px-3 sm:px-4 lg:px-6 xl:px-8 py-4 sm:py-6 lg:py-8">
        {/* Summary Stats */}
        <div className="grid grid-cols-2 sm:grid-cols-4 gap-3 sm:gap-4 mb-6">
          <div className="glass-card rounded-2xl p-4 sm:p-5 text-center">
            <FiUsers className="text-2xl text-[#3c79bb] mx-auto mb-2" />
            <div className="text-2xl sm:text-3xl font-bold text-gray-900">{totalUsers}</div>
            <div className="text-xs sm:text-sm text-gray-600 font-medium">Total Users</div>
          </div>
          <div className="glass-card rounded-2xl p-4 sm:p-5 text-center">
            <FiBook className="text-2xl text-[#3c79bb] mx-auto mb-2" />
            <div className="text-2xl sm:text-3xl font-bold text-gray-900">{data.totalTrainings}</div>
            <div className="text-xs sm:text-sm text-gray-600 font-medium">Total Trainings</div>
          </div>
          <div className="glass-card rounded-2xl p-4 sm:p-5 text-center">
            <FiAward className="text-2xl text-green-600 mx-auto mb-2" />
            <div className="text-2xl sm:text-3xl font-bold text-green-600">{totalCertificates}</div>
            <div className="text-xs sm:text-sm text-gray-600 font-medium">Certificates Issued</div>
          </div>
          <div className="glass-card rounded-2xl p-4 sm:p-5 text-center">
            <FiClock className="text-2xl text-orange-500 mx-auto mb-2" />
            <div className="text-2xl sm:text-3xl font-bold text-orange-500">{usersInProgress}</div>
            <div className="text-xs sm:text-sm text-gray-600 font-medium">Users In Progress</div>
          </div>
        </div>

        {/* Filters */}
        <div className="glass-card rounded-2xl p-4 sm:p-5 mb-6">
          <div className="flex flex-col sm:flex-row gap-3 sm:gap-4">
            <div className="flex-1 relative">
              <FiSearch className="absolute left-3 top-1/2 -translate-y-1/2 text-gray-400" />
              <input
                type="text"
                value={searchTerm}
                onChange={(e) => setSearchTerm(e.target.value)}
                placeholder="Search users..."
                className="w-full pl-10 pr-4 py-2.5 bg-white/80 border border-gray-300 rounded-xl focus:ring-2 focus:ring-[#3c79bb] focus:border-[#3c79bb] text-gray-900 font-medium placeholder-gray-400"
              />
            </div>
            <select
              value={filterStatus}
              onChange={(e) => setFilterStatus(e.target.value)}
              className="px-4 py-2.5 bg-white/80 border border-gray-300 rounded-xl focus:ring-2 focus:ring-[#3c79bb] text-gray-900 font-medium"
            >
              <option value="all">All Users</option>
              <option value="completed">Has Completions</option>
              <option value="inProgress">In Progress</option>
              <option value="notStarted">Not Started</option>
            </select>
          </div>
        </div>

        {/* Users List */}
        <div className="space-y-3">
          {filteredUsers.length === 0 ? (
            <div className="glass-card rounded-2xl p-8 text-center">
              <FiUsers className="text-4xl text-gray-400 mx-auto mb-3" />
              <p className="text-gray-600 font-medium">No users match your filters</p>
            </div>
          ) : (
            filteredUsers.map((userData) => {
              const isExpanded = expandedUsers.has(userData.user.id);
              return (
                <div key={userData.user.id} className="glass-card rounded-2xl overflow-hidden">
                  <button
                    onClick={() => toggleUser(userData.user.id)}
                    className="w-full flex items-center gap-4 p-4 sm:p-5 hover:bg-white/30 transition-colors text-left"
                  >
                    {/* User avatar */}
                    {userData.user.profileImage ? (
                      <img
                        src={getImageUrl(userData.user.profileImage)}
                        alt={getUserDisplayName(userData.user)}
                        className="w-10 h-10 sm:w-12 sm:h-12 rounded-full object-cover ring-2 ring-white/50 flex-shrink-0"
                      />
                    ) : (
                      <div className="w-10 h-10 sm:w-12 sm:h-12 rounded-full bg-[#3c79bb] flex items-center justify-center text-white font-semibold ring-2 ring-white/50 flex-shrink-0">
                        {(userData.user.firstName?.[0] || userData.user.username?.[0] || 'U').toUpperCase()}
                      </div>
                    )}

                    {/* User info */}
                    <div className="flex-1 min-w-0">
                      <div className="flex items-center gap-2 flex-wrap">
                        <h3 className="font-bold text-gray-900 text-sm sm:text-base truncate">
                          {getUserDisplayName(userData.user)}
                        </h3>
                        <span className="px-2 py-0.5 text-[10px] font-semibold rounded-full bg-[#3c79bb]/10 text-[#3c79bb]">
                          {userData.user.role?.toUpperCase()}
                        </span>
                      </div>
                      <p className="text-xs sm:text-sm text-gray-500 truncate">{userData.user.email}</p>
                    </div>

                    {/* Completion badges */}
                    <div className="flex items-center gap-2 sm:gap-4 flex-shrink-0">
                      {userData.totalCompleted > 0 && (
                        <div className="flex items-center gap-1 text-green-600">
                          <FiAward className="text-base sm:text-lg" />
                          <span className="font-bold text-sm">{userData.totalCompleted}</span>
                        </div>
                      )}
                      {userData.totalInProgress > 0 && (
                        <div className="flex items-center gap-1 text-orange-500">
                          <FiClock className="text-base sm:text-lg" />
                          <span className="font-bold text-sm">{userData.totalInProgress}</span>
                        </div>
                      )}
                      {userData.totalCompleted === 0 && userData.totalInProgress === 0 && (
                        <span className="text-xs text-gray-400 font-medium">Not started</span>
                      )}
                      {isExpanded ? (
                        <FiChevronUp className="text-gray-500" />
                      ) : (
                        <FiChevronDown className="text-gray-500" />
                      )}
                    </div>
                  </button>

                  {/* Expanded details */}
                  {isExpanded && (
                    <div className="px-4 sm:px-5 pb-4 sm:pb-5 border-t border-white/20">
                      {/* Completed trainings */}
                      {userData.completedTrainings.length > 0 && (
                        <div className="mt-4">
                          <h4 className="text-sm font-bold text-green-700 mb-3 flex items-center gap-2">
                            <FiCheckCircle />
                            Completed Trainings ({userData.completedTrainings.length})
                          </h4>
                          <div className="space-y-2">
                            {userData.completedTrainings.map((ct) => (
                              <div
                                key={ct.trainingId}
                                className="flex items-center justify-between p-3 bg-green-50/50 rounded-xl border border-green-200/50"
                              >
                                <div className="flex items-center gap-3">
                                  <FiAward className="text-green-600 flex-shrink-0" />
                                  <div>
                                    <span className="font-semibold text-gray-900 text-sm">{ct.trainingTitle}</span>
                                    <p className="text-xs text-gray-500">
                                      Completed {new Date(ct.completedAt).toLocaleDateString('en-US', { month: 'short', day: 'numeric', year: 'numeric' })}
                                    </p>
                                  </div>
                                </div>
                                <button
                                  onClick={() => {
                                    setSelectedCompletion({
                                      certificate_id: ct.certificateId,
                                      training_title: ct.trainingTitle,
                                      user_name: getUserDisplayName(userData.user),
                                      completed_at: ct.completedAt
                                    });
                                    setShowCertificate(true);
                                  }}
                                  className="flex items-center gap-1 px-3 py-1.5 bg-green-600 text-white rounded-lg hover:bg-green-700 transition-colors text-xs font-semibold"
                                >
                                  <FiAward />
                                  Certificate
                                </button>
                              </div>
                            ))}
                          </div>
                        </div>
                      )}

                      {/* In-progress trainings */}
                      {userData.inProgressTrainings.length > 0 && (
                        <div className="mt-4">
                          <h4 className="text-sm font-bold text-orange-600 mb-3 flex items-center gap-2">
                            <FiClock />
                            In Progress ({userData.inProgressTrainings.length})
                          </h4>
                          <div className="space-y-2">
                            {userData.inProgressTrainings.map((ip) => (
                              <div
                                key={ip.trainingId}
                                className="flex items-center justify-between p-3 bg-orange-50/50 rounded-xl border border-orange-200/50"
                              >
                                <div className="flex-1">
                                  <span className="font-semibold text-gray-900 text-sm">{ip.trainingTitle}</span>
                                  <p className="text-xs text-gray-500">
                                    {ip.completedModules} of {ip.totalModules} modules completed
                                  </p>
                                </div>
                                <div className="flex items-center gap-3 flex-shrink-0">
                                  <div className="w-24 h-2 bg-gray-200 rounded-full overflow-hidden">
                                    <div
                                      className="h-full bg-gradient-to-r from-orange-400 to-orange-500 transition-all"
                                      style={{ width: `${ip.percentage}%` }}
                                    ></div>
                                  </div>
                                  <span className="text-xs font-bold text-orange-600 w-10 text-right">{ip.percentage}%</span>
                                </div>
                              </div>
                            ))}
                          </div>
                        </div>
                      )}

                      {/* Not started */}
                      {userData.totalCompleted === 0 && userData.totalInProgress === 0 && (
                        <div className="mt-4 text-center py-6">
                          <p className="text-gray-500 text-sm font-medium">This user hasn't started any trainings yet.</p>
                        </div>
                      )}
                    </div>
                  )}
                </div>
              );
            })
          )}
        </div>
      </main>

      {/* Certificate Modal */}
      {showCertificate && selectedCompletion && (
        <CertificateModal
          completion={selectedCompletion}
          onClose={() => {
            setShowCertificate(false);
            setSelectedCompletion(null);
          }}
        />
      )}
    </div>
  );
}

export default TrainingCompletions;
